import { fetchGameFiltersHandler } from '~/handlers/fetch-game-filters.handler';
import { useModalStore } from '@/stores/apostatudo/modal';
import AnnouncementModal from '@/components/apostatudo/organisms/AnnouncementModal.vue';

const isPreviewOpen = ref(false);
const selectedGame = ref<Game | null>(null);
// Memoriza o jogo que entrou no iframe vindo do preview, pra reabrir o
// preview quando o usuário voltar do iframe (botão voltar da games-bar).
const lastPreviewGame = ref<Game | null>(null);
// `true` enquanto o enriquecimento esta em voo. A escolha de carteira depende
// do `bonus_eligible`, entao o sheet trava os botoes ate isto resolver — abrir
// o jogo antes prende a sessao na carteira errada, e nao ha como trocar sem
// relançar.
const isEnrichingGame = ref(false);

// Enriquecer jogo parcial: alguns pontos de entrada (LastGamesPlayed,
// RecentWinners, biggestWinners) só têm dados básicos do jogo (sem tags
// nem ribbon). Busca o jogo completo por slug e mescla os campos que
// faltam no selectedGame, sem bloquear a abertura do modal.
//
// `bonus_eligible` entra nessa conta: no mobile a escolha de carteira acontece
// AQUI, antes do iframe, então o preview precisa saber se o jogo aceita bônus
// mesmo quando o card de origem não mandou o campo. Sem isso o botão verde
// nunca apareceria para quem entra pelos "últimos jogados" ou pelos ganhadores
// — e o jogador cairia no iframe já sem chance de escolher.
async function enrichSelectedGame(game: Game) {
  if (!game?.slug) return;
  if (
    (game as any).tags &&
    (game as any).ribbon !== undefined &&
    (game as any).bonus_eligible !== undefined
  ) {
    return;
  }

  isEnrichingGame.value = true;

  try {
    const targetSlug = String(game.slug).toLowerCase();
    const { filterData } = await fetchGameFiltersHandler({
      term: game.name || targetSlug,
      page: 1,
      per_page: 20,
    });

    const match = filterData?.data?.find(
      (g: any) => String(g.slug ?? '').toLowerCase() === targetSlug,
    );

    if (!match) return;
    if (selectedGame.value?.slug?.toLowerCase() !== targetSlug) return;

    selectedGame.value = {
      ...selectedGame.value,
      tags: (selectedGame.value as any).tags ?? (match as any).tags ?? null,
      ribbon:
        (selectedGame.value as any).ribbon ?? (match as any).ribbon ?? null,
      provider: selectedGame.value.provider?.name
        ? selectedGame.value.provider
        : ((match as any).provider ?? selectedGame.value.provider),
      bonus_eligible:
        (selectedGame.value as any).bonus_eligible ??
        (match as any).bonus_eligible,
    } as Game;
  } catch {
  } finally {
    isEnrichingGame.value = false;
  }
}

export function useGameMobilePreview() {
  function openPreview(game: Game) {
    // Apostas pausadas no core (Operações do site, ENG-915): no mobile o
    // jogo abre por preview, sem rota, e o middleware block-games-sports não
    // pega. openPreview é o ponto por onde passam todas as aberturas.
    const { isGamesPaused } = useFeatureFlagsNew();

    if (isGamesPaused.value) {
      const { open } = useModalStore();

      open(AnnouncementModal, { variant: 'games' });

      return;
    }

    selectedGame.value = game;
    isPreviewOpen.value = true;
    enrichSelectedGame(game);
  }

  function closePreview() {
    isPreviewOpen.value = false;
    selectedGame.value = null;
  }

  function rememberAsLastPreview(game: Game) {
    lastPreviewGame.value = game;
  }

  function reopenLastPreview(): boolean {
    const game = lastPreviewGame.value;

    if (!game) return false;
    selectedGame.value = game;
    isPreviewOpen.value = true;
    lastPreviewGame.value = null;

    return true;
  }

  function clearLastPreview() {
    lastPreviewGame.value = null;
  }

  return {
    isPreviewOpen,
    selectedGame,
    isEnrichingGame,
    lastPreviewGame,
    openPreview,
    closePreview,
    rememberAsLastPreview,
    reopenLastPreview,
    clearLastPreview,
  };
}
